import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import ScrollToTopButton from '../../components/feature/ScrollToTopButton';

const ContactoPage = () => {
  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    tipoProyecto: '',
    metrosCuadrados: '',
    distrito: '',
    mensaje: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    document.title = 'Contacto - Presupuesto de Pladur en Barcelona | PLADUREX';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Contacta con PLADUREX para tu proyecto de pladur en Barcelona. Carrer de Garcilaso 114, Sant Andreu. Tel: +34 629 501 941. Presupuesto sin compromiso.');
    }

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://barcelona.pladures.es';

    // Add ContactPage Schema
    const contactPageSchema = {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Contacto - Presupuesto de Pladur en Barcelona",
      "description": "Contacta con PLADUREX para tu proyecto de pladur en Barcelona. Presupuesto sin compromiso.",
      "url": `${siteUrl}/contacto`,
      "inLanguage": "es-ES",
      "isPartOf": {
        "@type": "WebSite",
        "name": "PLADUREX Barcelona",
        "url": siteUrl
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Inicio",
            "item": `${siteUrl}/`
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Contacto",
            "item": `${siteUrl}/contacto`
          }
        ]
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(contactPageSchema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d60bnidcojuehkk4lm0g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nombre: '',
          telefono: '',
          tipoProyecto: '',
          metrosCuadrados: '',
          distrito: '',
          mensaje: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative pt-28 pb-16 sm:pt-32 sm:pb-20 bg-gradient-to-br from-primary-dark to-primary overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img
              src="https://public.readdy.ai/ai/img_res/c343b9a0bb9bbd9ff4bed27e6076a3bb.jpg"
              alt="Contacto"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-white font-extrabold text-3xl sm:text-5xl md:text-6xl mb-6"
            >
              Contacta con nosotros
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-white/90 text-xl leading-relaxed max-w-3xl mx-auto"
            >
              Solicita tu presupuesto sin compromiso para tu proyecto de <Link to="/servicios" className="text-white underline hover:text-white/80">instalación de pladur en Barcelona</Link>. Te respondemos en menos de 24 horas.
            </motion.p>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-24 bg-bg-light">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              {/* Contact Info */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-primary-dark text-4xl font-serif font-bold mb-8">
                  Información de contacto
                </h2>

                <div className="space-y-8">
                  <div>
                    <div className="inline-block bg-primary text-white text-xs font-bold uppercase px-3 py-1 rounded mb-3">
                      Dirección
                    </div>
                    <p className="text-gray-700 text-lg leading-relaxed">
                      Carrer de Garcilaso, 114<br />
                      Sant Andreu, 08027 Barcelona
                    </p>
                  </div>

                  <div>
                    <div className="inline-block bg-primary text-white text-xs font-bold uppercase px-3 py-1 rounded mb-3">
                      Teléfono
                    </div>
                    <a
                      href="tel:+34629501941"
                      className="block text-primary text-2xl font-bold hover:text-primary-dark transition-colors duration-300"
                    >
                      +34 629 501 941
                    </a>
                  </div>

                  <div>
                    <div className="inline-block bg-primary text-white text-xs font-bold uppercase px-3 py-1 rounded mb-3">
                      WhatsApp
                    </div>
                    <a
                      href="https://wa.me/34629501941"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-2 text-[#25D366] text-lg font-bold hover:underline"
                    >
                      <i className="ri-whatsapp-line text-2xl"></i>
                      <span>Enviar mensaje</span>
                    </a>
                  </div>

                  <div>
                    <div className="inline-block bg-primary text-white text-xs font-bold uppercase px-3 py-1 rounded mb-3">
                      Horario
                    </div>
                    <p className="text-gray-700 text-lg">
                      Lunes a Viernes: 08:00 - 18:00<br />
                      Sábados y Domingos: Consultar
                    </p>
                  </div>
                </div>

                <div className="mt-12">
                  <h3 className="text-primary-dark text-2xl font-bold mb-6">
                    Zonas de servicio
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    {[
                      'Sant Andreu',
                      'Eixample',
                      'Sants',
                      'Les Corts',
                      'Gràcia',
                      'Sant Martí',
                      'Ciutat Vella',
                      'Horta-Guinardó',
                      'Nou Barris',
                      'Poblenou',
                    ].map((zone, index) => (
                      <span
                        key={index}
                        className="bg-white px-4 py-2 rounded-full text-sm font-medium text-gray-700 shadow-sm"
                      >
                        {zone}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mt-8">
                  <p className="text-gray-600 text-sm">
                    Antes de contactar, quizás te interese consultar nuestras <Link to="/faq" className="text-primary hover:underline font-semibold">preguntas frecuentes</Link> o conocer más <Link to="/empresa" className="text-primary hover:underline font-semibold">sobre nuestra empresa</Link>.
                  </p>
                </div>

                <div className="mt-8">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20construction%20worker%20with%20tools%20and%20materials%20ready%20for%20drywall%20installation%20smiling%20confident%20portrait%20clean%20professional%20atmosphere%20high%20quality%20photography&width=800&height=600&seq=contacto-img-001&orientation=landscape"
                    alt="Equipo PLADUREX"
                    className="w-full h-80 object-cover object-top rounded-2xl shadow-lg"
                  />
                </div>
              </motion.div>

              {/* Contact Form */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="bg-white rounded-2xl shadow-lg p-8 lg:p-10">
                  <h3 className="text-primary-dark text-3xl font-bold mb-6">
                    Solicita tu presupuesto
                  </h3>
                  <p className="text-gray-600 text-[15px] mb-8">
                    Completa el formulario y nos pondremos en contacto contigo en menos de 24 horas para ofrecerte un presupuesto personalizado. Consulta nuestros <Link to="/servicios" className="text-primary hover:underline font-semibold">servicios de pladur</Link> disponibles.
                  </p>

                  <form id="contacto-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label htmlFor="nombre" className="block text-gray-700 font-semibold mb-2 text-sm">
                        Nombre completo *
                      </label>
                      <input
                        type="text"
                        id="nombre"
                        name="nombre"
                        value={formData.nombre}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300 text-sm"
                        placeholder="Tu nombre"
                      />
                    </div>

                    <div>
                      <label htmlFor="telefono" className="block text-gray-700 font-semibold mb-2 text-sm">
                        Teléfono *
                      </label>
                      <input
                        type="tel"
                        id="telefono"
                        name="telefono"
                        value={formData.telefono}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300 text-sm"
                        placeholder="+34 600 000 000"
                      />
                    </div>

                    <div>
                      <label htmlFor="tipoProyecto" className="block text-gray-700 font-semibold mb-2 text-sm">
                        Tipo de proyecto *
                      </label>
                      <select
                        id="tipoProyecto"
                        name="tipoProyecto"
                        value={formData.tipoProyecto}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300 text-sm"
                      >
                        <option value="">Selecciona una opción</option>
                        <option value="Tabiques de pladur">Tabiques de pladur</option>
                        <option value="Trasdosados">Trasdosados</option>
                        <option value="Falsos techos">Falsos techos</option>
                        <option value="Aislamiento acústico">Aislamiento acústico</option>
                        <option value="Aislamiento térmico">Aislamiento térmico</option>
                        <option value="Reforma completa">Reforma completa</option>
                        <option value="Otro">Otro</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="metrosCuadrados" className="block text-gray-700 font-semibold mb-2 text-sm">
                        Metros cuadrados aproximados
                      </label>
                      <input
                        type="text"
                        id="metrosCuadrados"
                        name="metrosCuadrados"
                        value={formData.metrosCuadrados}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300 text-sm"
                        placeholder="Ej: 30 m²"
                      />
                    </div>

                    <div>
                      <label htmlFor="distrito" className="block text-gray-700 font-semibold mb-2 text-sm">
                        Distrito de Barcelona
                      </label>
                      <input
                        type="text"
                        id="distrito"
                        name="distrito"
                        value={formData.distrito}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300 text-sm"
                        placeholder="Ej: Sant Andreu, Eixample..."
                      />
                    </div>

                    <div>
                      <label htmlFor="mensaje" className="block text-gray-700 font-semibold mb-2 text-sm">
                        Mensaje *
                      </label>
                      <textarea
                        id="mensaje"
                        name="mensaje"
                        value={formData.mensaje}
                        onChange={handleChange}
                        required
                        maxLength={500}
                        rows={5}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300 resize-none text-sm"
                        placeholder="Cuéntanos más sobre tu proyecto (máximo 500 caracteres)"
                      ></textarea>
                      <p className="text-gray-500 text-xs mt-1">
                        {formData.mensaje.length}/500 caracteres
                      </p>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-primary text-white py-4 rounded-lg font-bold text-lg hover:bg-primary-dark transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {isSubmitting ? 'Enviando...' : 'Enviar Solicitud'}
                    </button>

                    {submitStatus === 'success' && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm"
                      >
                        <strong>¡Mensaje enviado!</strong> Nos pondremos en contacto contigo pronto.
                      </motion.div>
                    )}

                    {submitStatus === 'error' && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm"
                      >
                        <strong>Error al enviar.</strong> Por favor, inténtalo de nuevo o llámanos directamente.
                      </motion.div>
                    )}
                  </form>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Map Section */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-primary-dark text-4xl md:text-5xl font-serif font-bold mb-6">
                Nuestra ubicación
              </h2>
              <p className="text-gray-600 text-lg">
                Nos encontramos en Sant Andreu, Barcelona. Desde aquí atendemos toda la ciudad y área metropolitana con nuestros <Link to="/servicios" className="text-primary hover:underline font-semibold">servicios de pladur profesionales</Link>.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="rounded-2xl overflow-hidden shadow-2xl"
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2992.8947891234567!2d2.189306!3d41.435459!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDHCsDI2JzA3LjciTiAywrAxMScyMS41IkU!5e0!3m2!1ses!2ses!4v1234567890"
                width="100%"
                height="500"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Ubicación PLADUREX en Barcelona"
              ></iframe>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
      <ScrollToTopButton />
    </div>
  );
};

export default ContactoPage;
